import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../components/AppToast";
import { TYPE_ICON, DEFAULT_MAX_ORG_LEVEL, ORG_TYPES_MASTER } from "./orgConstants";
import {
  buildTreeWithMeta,
  buildTreeWithMetaAndSuppliers,
  collectIds,
  filterTree,
  isSupplierTreeNode,
  resolveSupplierTreeSelection,
  validateReparent,
} from "./orgTreeUtils";
import { getOrgCustomers } from "./customerModel";

function TreeRows({
  nodes,
  depth,
  expanded,
  toggleExpand,
  selectedId,
  onSelect,
  onContextNode,
  levels,
}) {
  return (
    <ul className={`list-unstyled mb-0 ${depth > 0 ? "ms-2 border-start border-2 ps-2 org-tree-connector" : ""}`}>
      {nodes.map((node) => {
        const isSup = isSupplierTreeNode(node);
        const hasKids = !isSup && (node.children || []).length > 0;
        const open = expanded.has(node.id);
        const isSel = selectedId === node.id;
        const level = levels[node.id] ?? depth + 1;
        const levelHue = isSup ? 28 : 220 - Math.min(level * 18, 120);
        return (
          <li key={node.id} className="mb-0">
            <div
              className={`org-tree-node d-flex align-items-center gap-1 py-1 px-2 ${isSel ? "selected" : ""} ${isSup ? "org-tree-node--supplier" : ""}`}
              style={{ paddingLeft: `${2 + depth * 6}px` }}
              onClick={() => onSelect(node.id)}
              onContextMenu={(e) => onContextNode(e, node)}
            >
              <span
                className="org-tree-level-dot flex-shrink-0"
                style={{ background: `hsl(${levelHue} ${isSup ? "65%" : "70%"} ${isSup ? "48%" : "52%"})` }}
                title={isSup ? "Supplier under organization" : `Level ${level}`}
              />
              {hasKids ? (
                <button
                  type="button"
                  className="btn btn-sm btn-light border-0 p-0 lh-1 text-secondary"
                  style={{ width: "1.1rem" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(node.id);
                  }}
                  aria-expanded={open}
                  aria-label={open ? "Collapse" : "Expand"}
                >
                  <i className={`bi ${open ? "bi-caret-down-fill" : "bi-caret-right-fill"} small`} />
                </button>
              ) : (
                <span style={{ width: "1.1rem" }} className="d-inline-block flex-shrink-0" />
              )}
              <span className="org-tree-type-icon flex-shrink-0" aria-hidden>
                {isSup ? TYPE_ICON.Supplier : TYPE_ICON[node.type] || "📁"}
              </span>
              <div className="flex-grow-1 text-truncate small">
                {isSup ? (
                  <span className="text-muted text-uppercase x-small me-1 org-tree-supplier-tag">Supplier</span>
                ) : null}
                <span className="fw-semibold text-body">{node.code}</span>
                <span className="text-muted mx-1">·</span>
                <span>{node.name}</span>
                <span
                  className={`badge ms-2 align-middle ${
                    String(node.status).toLowerCase() === "active"
                      ? "text-bg-success-subtle text-success"
                      : "text-bg-secondary-subtle text-secondary"
                  }`}
                >
                  {node.status}
                </span>
              </div>
            </div>
            {hasKids && open && (
              <TreeRows
                nodes={node.children}
                depth={depth + 1}
                expanded={expanded}
                toggleExpand={toggleExpand}
                selectedId={selectedId}
                onSelect={onSelect}
                onContextNode={onContextNode}
                levels={levels}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

const emptyDetail = {
  code: "",
  name: "",
  shortName: "",
  type: "Company",
  parentId: null,
  status: "Active",
  legalEntity: false,
  operationalEntity: true,
};

export default function OrganizationHierarchy({
  orgFlat,
  setOrgFlat,
  maxLevel = DEFAULT_MAX_ORG_LEVEL,
  /** When set, called after validation and before local state update (e.g. reparent + update APIs). */
  onPersistNodeToApi,
}) {
  const { tree, levels, paths } = useMemo(() => buildTreeWithMetaAndSuppliers(orgFlat), [orgFlat]);
  const supplierTotal = useMemo(() => orgFlat.reduce((sum, row) => sum + getOrgCustomers(row).length, 0), [orgFlat]);
  const [searchQuery, setSearchQuery] = useState("");
  const filteredTree = useMemo(() => filterTree(tree, searchQuery), [tree, searchQuery]);
  const [expanded, setExpanded] = useState(() => new Set(collectIds(tree)));
  const didInitExpandRef = useRef(false);
  const [selectedId, setSelectedId] = useState(orgFlat[0]?.id ?? null);
  const [detailEdit, setDetailEdit] = useState(false);
  const [detailForm, setDetailForm] = useState(emptyDetail);

  const [ctx, setCtx] = useState(null);

  const supplierSelection = useMemo(() => resolveSupplierTreeSelection(orgFlat, selectedId ?? ""), [orgFlat, selectedId]);

  useEffect(() => {
    if (resolveSupplierTreeSelection(orgFlat, selectedId ?? "")) {
      setDetailEdit(false);
      return;
    }
    const n = orgFlat.find((x) => x.id === selectedId);
    if (n) {
      setDetailForm({
        code: n.code,
        name: n.name,
        shortName: n.shortName ?? "",
        type: n.type,
        parentId: n.parentId,
        status: n.status,
        legalEntity: !!n.legalEntity,
        operationalEntity: !!n.operationalEntity,
      });
    }
  }, [selectedId, orgFlat]);

  useEffect(() => {
    const close = () => setCtx(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  /** First time the tree has rows (e.g. after API load), expand all so nested suppliers are visible. */
  useEffect(() => {
    if (didInitExpandRef.current) return;
    const ids = collectIds(tree);
    if (ids.length === 0) return;
    didInitExpandRef.current = true;
    setExpanded(new Set(ids));
  }, [tree]);

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpanded(new Set(collectIds(tree)));
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpanded(new Set());
  }, []);

  const onContextNode = useCallback((e, node) => {
    e.preventDefault();
    e.stopPropagation();
    setCtx({ x: e.clientX, y: e.clientY, node });
  }, []);

  const saveDetail = useCallback(async () => {
    if (!selectedId) return;
    const codeTrim = detailForm.code.trim();
    const dup = orgFlat.some((x) => x.id !== selectedId && x.code === codeTrim);
    if (dup) {
      toast.error("Organization code must be unique within the tenant.");
      return;
    }
    const v = validateReparent(orgFlat, selectedId, detailForm.parentId, maxLevel);
    if (!v.ok) {
      toast.error(v.reason);
      return;
    }
    const prevRow = orgFlat.find((x) => x.id === selectedId);
    if (!prevRow) {
      toast.error("Selected organization was not found.");
      return;
    }
    const mergedRow = {
      ...prevRow,
      code: codeTrim,
      name: detailForm.name.trim(),
      shortName: detailForm.shortName.trim(),
      type: detailForm.type,
      parentId: detailForm.parentId,
      status: detailForm.status,
      legalEntity: detailForm.legalEntity,
      operationalEntity: detailForm.operationalEntity,
    };
    if (onPersistNodeToApi) {
      try {
        await onPersistNodeToApi(mergedRow, prevRow);
      } catch (err) {
        const msg = typeof err === "string" ? err : err?.message || "Save failed.";
        toast.error(String(msg));
        return;
      }
    }
    setOrgFlat((prev) => {
      const next = prev.map((row) => (row.id === selectedId ? mergedRow : row));
      const { levels, paths } = buildTreeWithMeta(next);
      return next.map((r) => ({
        ...r,
        hierarchyLevel: levels[r.id],
        hierarchyPath: paths[r.id],
      }));
    });
    toast.success("Organization details saved.");
    setDetailEdit(false);
  }, [selectedId, detailForm, orgFlat, setOrgFlat, maxLevel, onPersistNodeToApi]);

  const selected = orgFlat.find((x) => x.id === selectedId);
  const parentOptions = orgFlat.filter((x) => x.id !== selectedId);

  const mobilePickerValue = selectedId ?? "";

  return (
    <div className="org-hierarchy-split row g-0 rounded-3 border shadow-sm overflow-hidden bg-white">
      <div className="col-12 col-md-5 col-lg-4 org-tree-panel d-flex flex-column">
        <div className="p-3 border-bottom bg-white">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0 fw-semibold">
              <i className="bi bi-diagram-3 me-2 text-primary" aria-hidden />
              Organization tree
            </h6>
            <span className="badge text-bg-light border">
              {orgFlat.length} org{orgFlat.length !== 1 ? "s" : ""}
              {supplierTotal > 0 ? ` · ${supplierTotal} supplier${supplierTotal !== 1 ? "s" : ""}` : ""}
            </span>
          </div>
          <div className="input-group input-group-sm mb-2">
            <span className="input-group-text bg-white">
              <i className="bi bi-search" />
            </span>
            <input
              className="form-control"
              placeholder="Search code or name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={expandAll}>
              Expand all
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={collapseAll}>
              Collapse all
            </button>
          </div>
        </div>

        <div className="d-md-none p-2 border-bottom bg-white">
          <label className="form-label small text-muted mb-1">Jump to organization</label>
          <select
            className="form-select form-select-sm"
            value={mobilePickerValue}
            onChange={(e) => setSelectedId(e.target.value || null)}
          >
            {orgFlat.map((n) => (
              <option key={n.id} value={n.id}>
                {TYPE_ICON[n.type]} {n.code} — {n.name}
              </option>
            ))}
          </select>
        </div>

        <div className="org-tree-scroll flex-grow-1 p-2 d-none d-md-block">
          <div className="rounded-2 p-1">
            {filteredTree.length === 0 ? (
              <p className="text-muted small mb-0 px-2 py-3">No organizations match your search.</p>
            ) : (
              <TreeRows
                nodes={filteredTree}
                depth={0}
                expanded={expanded}
                toggleExpand={toggleExpand}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onContextNode={onContextNode}
                levels={levels}
              />
            )}
          </div>
          <p className="text-muted x-small mt-2 mb-0 px-1 small">
            <i className="bi bi-pencil-square me-1" />
            Reparent organizations via <strong>Edit</strong> and <strong>Parent organization</strong>. Suppliers appear under
            their linked org (read-only here).
          </p>
        </div>
      </div>

      <div className="col-12 col-md-7 col-lg-8 org-detail-panel d-flex flex-column">
        <div className="p-3 border-bottom">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <h6 className="mb-1 fw-semibold">{supplierSelection ? "Selected supplier" : "Selected organization"}</h6>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0 small">
                  {(paths[selectedId] || "—").split(" / ").map((crumb, i, arr) => (
                    <li
                      key={`${crumb}-${i}`}
                      className={`breadcrumb-item ${i === arr.length - 1 ? "active" : ""}`}
                      aria-current={i === arr.length - 1 ? "page" : undefined}
                    >
                      {crumb}
                    </li>
                  ))}
                </ol>
              </nav>
            </div>
            {!supplierSelection ? (
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn btn-sm ${!detailEdit ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setDetailEdit(false)}
                >
                  View
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${detailEdit ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setDetailEdit(true)}
                >
                  Edit
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="p-3 flex-grow-1 overflow-auto">
          {!selectedId ? (
            <p className="text-muted">Select a node in the tree.</p>
          ) : supplierSelection ? (
            <>
              <div className="card border org-supplier-detail-card shadow-sm mb-3">
                <div className="card-body">
                  <p className="small text-muted mb-3">
                    Linked organization:{" "}
                    <strong className="text-body">
                      {supplierSelection.org.code} — {supplierSelection.org.name}
                    </strong>
                  </p>
                  <div className="row g-3 small">
                    <div className="col-md-6">
                      <div className="text-muted text-uppercase x-small mb-1">Supplier ID</div>
                      <div className="fw-medium text-break">{supplierSelection.supplier.customerId || "—"}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted text-uppercase x-small mb-1">Status</div>
                      <div className="fw-medium">{supplierSelection.supplier.customerStatus || "—"}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted text-uppercase x-small mb-1">Code</div>
                      <div className="fw-medium">{supplierSelection.supplier.customerCode || "—"}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted text-uppercase x-small mb-1">Name</div>
                      <div className="fw-medium">{supplierSelection.supplier.customerName || "—"}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted text-uppercase x-small mb-1">Type</div>
                      <div>{supplierSelection.supplier.customerType || "—"}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted text-uppercase x-small mb-1">Group</div>
                      <div>{supplierSelection.supplier.customerGroup || "—"}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted text-uppercase x-small mb-1">Market / region</div>
                      <div>{supplierSelection.supplier.marketRegion || "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-muted small mb-0">
                <i className="bi bi-info-circle me-1" />
                To add or edit suppliers, open the organization in <strong>Organization management</strong> (supplier list /
                onboarding tabs).
              </p>
            </>
          ) : selected ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (detailEdit) await saveDetail();
              }}
            >
              <fieldset disabled={!detailEdit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small text-muted">Organization code</label>
                    <input
                      className="form-control form-control-sm"
                      value={detailForm.code}
                      onChange={(e) => setDetailForm((p) => ({ ...p, code: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted">Organization name</label>
                    <input
                      className="form-control form-control-sm"
                      value={detailForm.name}
                      onChange={(e) => setDetailForm((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small text-muted">Short name</label>
                    <input
                      className="form-control form-control-sm"
                      value={detailForm.shortName}
                      onChange={(e) => setDetailForm((p) => ({ ...p, shortName: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small text-muted">Type</label>
                    <select
                      className="form-select form-select-sm"
                      value={detailForm.type}
                      onChange={(e) => setDetailForm((p) => ({ ...p, type: e.target.value }))}
                      disabled={detailForm.type === "Tenant"}
                      title={
                        detailForm.type === "Tenant"
                          ? "Tenant is fixed for the system root organization."
                          : undefined
                      }
                    >
                      {detailForm.type === "Tenant" ? (
                        <option value="Tenant">
                          {TYPE_ICON.Tenant} Tenant (system root)
                        </option>
                      ) : (
                        ORG_TYPES_MASTER.map((t) => (
                          <option key={t.value} value={t.value}>
                            {TYPE_ICON[t.value]} {t.label}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small text-muted">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={detailForm.status}
                      onChange={(e) => setDetailForm((p) => ({ ...p, status: e.target.value }))}
                    >
                      {["Active", "Inactive", "Blocked", "Archived"].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label small text-muted">Parent organization</label>
                    <select
                      className="form-select form-select-sm"
                      value={detailForm.parentId ?? ""}
                      onChange={(e) =>
                        setDetailForm((p) => ({ ...p, parentId: e.target.value || null }))
                      }
                    >
                      <option value="">(Root)</option>
                      {parentOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {paths[p.id] ?? p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="det-legal"
                        checked={detailForm.legalEntity}
                        onChange={(e) => setDetailForm((p) => ({ ...p, legalEntity: e.target.checked }))}
                      />
                      <label className="form-check-label small" htmlFor="det-legal">
                        Legal entity
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="det-op"
                        checked={detailForm.operationalEntity}
                        onChange={(e) =>
                          setDetailForm((p) => ({ ...p, operationalEntity: e.target.checked }))
                        }
                      />
                      <label className="form-check-label small" htmlFor="det-op">
                        Operational entity
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted">Hierarchy level</label>
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={levels[selectedId] ?? "—"}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted">Mapped users (count)</label>
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={(selected.mappedUserIds || []).length}
                    />
                  </div>
                </div>
              </fieldset>
              {detailEdit && (
                <div className="mt-3 d-flex gap-2">
                  <button type="submit" className="btn btn-sm btn-primary">
                    Save node
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setDetailEdit(false);
                      const n = orgFlat.find((x) => x.id === selectedId);
                      if (n)
                        setDetailForm({
                          code: n.code,
                          name: n.name,
                          shortName: n.shortName ?? "",
                          type: n.type,
                          parentId: n.parentId,
                          status: n.status,
                          legalEntity: !!n.legalEntity,
                          operationalEntity: !!n.operationalEntity,
                        });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </form>
          ) : (
            <p className="text-muted">Select a node in the tree.</p>
          )}

          <div className={`card org-section-card mt-4 ${supplierSelection ? "opacity-75" : ""}`}>
            <div className="card-header py-2 px-3 small fw-semibold text-body-secondary">
              Pricing resolution (read-only)
            </div>
            <ul className="list-group list-group-flush org-pricing-priority">
              <li className="list-group-item">1. Supplier-specific pricing</li>
              <li className="list-group-item">2. Supplier-group pricing</li>
              <li className="list-group-item">3. Organization-level pricing</li>
              <li className="list-group-item text-danger">4. Error if not found</li>
            </ul>
          </div>

          <div className="alert alert-light border mt-3 small mb-0" role="note">
            <strong>Access scoping:</strong> mapped users inherit data visibility for this node and its
            descendants. Supplier mapping defines ownership for transactions and reporting.
          </div>
        </div>
      </div>

      {ctx && (
        <div
          className="org-context-menu list-group"
          style={{ left: ctx.x, top: ctx.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {isSupplierTreeNode(ctx.node) ? (
            <button
              type="button"
              className="list-group-item list-group-item-action py-2"
              onClick={() => {
                setSelectedId(ctx.node.id);
                setDetailEdit(false);
                setCtx(null);
              }}
            >
              <i className="bi bi-eye me-2" />
              View supplier
            </button>
          ) : (
            <>
              <button
                type="button"
                className="list-group-item list-group-item-action py-2"
                onClick={() => {
                  setSelectedId(ctx.node.id);
                  setDetailEdit(true);
                  setCtx(null);
                }}
              >
                <i className="bi bi-pencil me-2" />
                Edit node
              </button>
              <button
                type="button"
                className="list-group-item list-group-item-action py-2"
                onClick={() => {
                  setSelectedId(ctx.node.id);
                  setDetailEdit(false);
                  setCtx(null);
                }}
              >
                <i className="bi bi-eye me-2" />
                View node
              </button>
              <button
                type="button"
                className="list-group-item list-group-item-action py-2 text-muted"
                onClick={() => {
                  toast.info("Soft delete is only allowed when dependencies are resolved (UI placeholder).");
                  setCtx(null);
                }}
              >
                <i className="bi bi-trash me-2" />
                Delete (soft)
              </button>
            </>
          )}
        </div>
      )}

    </div>
  );
}
